/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import { InformationCircleIcon } from '@heroicons/react/24/outline'
import UiTableEmptyRow from 'components/Ui/UiTableEmptyRow'
import UiTableSkeleton from 'components/Ui/UiTableSkeleton'
import UiTableRowsList from './TableComponents/UiTableRowsList'
import { TableInitBase } from 'utils/hooks/useTable'

interface TableBodyProps {
    colSpan?: number
    tableInit: TableInitBase
    decorationId?: string
    isTableStateSame?: boolean
    onRowClick?: (rowData: unknown) => void
    onRowInteraction?: (rowData: unknown) => void
}

const TableBody = (props: TableBodyProps) => {
    const { t } = useTranslation()
    const rowsCount = props.tableInit.table.getRowModel().rows.length
    const shouldShowSkeleton =
        props.tableInit.showSkeletonLoading === true && rowsCount === 0
    const visibleLeafColumnsCount =
        props.tableInit.table.getVisibleLeafColumns().length ||
        props.tableInit.table.getAllLeafColumns().length ||
        1
    const effectiveColSpan = Math.max(
        props.colSpan ?? 0,
        visibleLeafColumnsCount
    )
    const skeletonRowsCount = Math.max(
        5,
        Math.min(10, Number(props.tableInit.pageSize) || 5)
    )

    const decorationId = props.decorationId ? props.decorationId : 'select'
    return (
        <tbody
            className={clsx(
                'TableBody relative h-full',
                rowsCount > 0 && 'zebra-rows',
                'bg-white text-gray-900 dark:bg-gray-950 dark:text-gray-100'
            )}
        >
            {!shouldShowSkeleton && (
                <UiTableRowsList
                    rows={props.tableInit.table.getRowModel().rows}
                    decorationId={decorationId}
                    onRowClick={props.onRowClick}
                    onRowInteraction={props.onRowInteraction}
                    hasRowsSelected={
                        props.tableInit.table.getIsSomeRowsSelected() ||
                        props.tableInit.table.getIsAllRowsSelected()
                    }
                />
            )}

            {/* not found */}
            {!props.tableInit.isLoading && !shouldShowSkeleton && rowsCount === 0 && (
                <UiTableEmptyRow
                    colSpan={effectiveColSpan}
                    icon={
                        <InformationCircleIcon
                            className={clsx('size-10 text-gray-400')}
                        />
                    }
                    title={t('No records found.')}
                />
            )}

            {/* centralized skeleton loader for all table loading interactions */}
            {shouldShowSkeleton && (
                <UiTableSkeleton
                    rows={skeletonRowsCount}
                    colSpan={effectiveColSpan}
                />
            )}

            {rowsCount > 0 && !shouldShowSkeleton && (
                <tr className="h-full bg-transparent!">
                    <td
                        className="p-0 border-none h-full"
                        colSpan={effectiveColSpan}
                    ></td>
                </tr>
            )}
        </tbody>
    )
}

export default TableBody
